import { FormEvent } from 'react';
import { FormControl, Glyphicon } from 'react-bootstrap';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import Button from 'components/button/Button';
import SelectField from 'components/forms/selectField/SelectField';
import {
  ConditionType,
  GuestsNumberOperator,
  IsCompleted,
  IsSigned,
  StayLengthOperator,
} from 'models/automation/Triggers';
import { TriggerAddEditModalFormValues } from '../TriggerAddEditModal.schema';
import { TriggerConditionFormValue } from '../TriggerAddEditModal.schema.utils';
import { buildCondition } from '../TriggerAddEditModal.utils';
import TriggerAddEditModalConditionsExtraFields from './TriggerAddEditModalConditionsExtraFields';
import useAvailableConditions from './useAvailableConditions';

interface TriggerAddEditModalChooseConditionFieldProps {
  index: number;
  onRemoveClick: () => void;
}

const getDefaultConditionByType = (
  conditionType: TriggerConditionFormValue['conditionType'],
): TriggerConditionFormValue => {
  switch (conditionType) {
    case ConditionType.AGREEMENT_SIGNED:
      return buildCondition({
        conditionType: ConditionType.AGREEMENT_SIGNED,
        operator: IsSigned.IS_SIGNED,
      });
    case ConditionType.BOOKING_PAID_IN_FULL:
      return buildCondition({
        conditionType: ConditionType.BOOKING_PAID_IN_FULL,
        operator: IsCompleted.IS_COMPLETED,
      });
    case ConditionType.CHANNEL:
      return buildCondition({
        all: false,
        channels: [],
        conditionType: ConditionType.CHANNEL,
      });
    case ConditionType.GUESTS_NUMBER:
      return buildCondition({
        conditionType: ConditionType.GUESTS_NUMBER,
        guests: 1,
        operator: GuestsNumberOperator.IS_GREATER_THAN,
      });
    case ConditionType.PRE_ARRIVAL_FORM_SUBMITTED:
      return buildCondition({
        conditionType: ConditionType.PRE_ARRIVAL_FORM_SUBMITTED,
        operator: IsCompleted.IS_COMPLETED,
      });
    case ConditionType.PROPERTY:
      return buildCondition({
        all: false,
        conditionType: ConditionType.PROPERTY,
        propertyUids: [],
      });
    case ConditionType.STAY_LENGTH:
      return buildCondition({
        conditionType: ConditionType.STAY_LENGTH,
        days: 1,
        operator: StayLengthOperator.IS_LONGER,
      });

    default:
      return buildCondition();
  }
};

const TriggerAddEditModalChooseConditionField = ({
  index,
  onRemoveClick,
}: TriggerAddEditModalChooseConditionFieldProps) => {
  const { t } = useTranslation();
  const { control, setValue } = useFormContext<TriggerAddEditModalFormValues>();
  const conditions = useWatch({ control, name: 'conditions' });
  const currentConditionType = conditions?.[index]?.conditionType;
  const availableConditions = useAvailableConditions();

  const filteredConditions = availableConditions.filter(
    (condition) =>
      condition === currentConditionType ||
      !conditions?.find((x) => x.conditionType === condition),
  );

  const conditionOptions = filteredConditions?.map((condition) => ({
    value: condition,
    label: t('pageAutomations.triggers.conditionType', { context: condition }),
  }));

  const options = [
    { label: t('pageAutomations.triggers.conditionType'), value: '' },
    ...(conditionOptions ?? []),
  ];

  const handleOnChange = (event: FormEvent<FormControl>) => {
    const { value } = event.target as HTMLSelectElement;

    if (value) {
      setValue(
        `conditions.${index}`,
        getDefaultConditionByType(value as ConditionType),
        { shouldDirty: true },
      );
    }
  };

  return (
    <div className="condition-row" data-testid="condition-row">
      <Button
        bsStyle="default"
        className="remove-condition"
        data-testid="remove-condition-button"
        onClick={onRemoveClick}
      >
        <Glyphicon glyph="remove" />
      </Button>
      <div className="condition-row__fields">
        <SelectField
          aria-label={t('pageAutomations.triggers.conditionType')}
          colSmInput={12}
          name={`conditions.${index}.conditionType`}
          onChangeHandler={handleOnChange}
          options={options}
        />
        {!!currentConditionType && (
          <TriggerAddEditModalConditionsExtraFields
            conditionType={currentConditionType}
            index={index}
          />
        )}
      </div>
    </div>
  );
};

export default TriggerAddEditModalChooseConditionField;
